import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Folder,
  FileText,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  RefreshCw,
  Loader2,
  Server,
  X,
  HardDrive,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const FileItem = ({ item, onSelect, onNavigate }) => {
  const isFile = item.type === "file";
  
  return (
    <div
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors",
        isFile && "hover:bg-primary/5"
      )}
      onClick={() => isFile ? onSelect(item) : onNavigate(item.path)}
      data-testid={`external-file-${item.name}`}
    >
      {isFile ? (
        <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      ) : (
        <Folder className="w-4 h-4 text-primary/70 flex-shrink-0" />
      )}
      <span className="flex-1 truncate text-sm">{item.name}</span>
      {!isFile && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      {isFile && item.size > 0 && (
        <span className="text-xs text-muted-foreground">
          {(item.size / 1024).toFixed(1)} KB
        </span>
      )}
    </div>
  );
};

export const ExternalFileBrowser = ({ 
  isOpen, 
  onClose, 
  connection,
  onFileSelect 
}) => {
  const [currentPath, setCurrentPath] = useState("/");
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pathHistory, setPathHistory] = useState(["/"]);

  const fetchFiles = useCallback(async (path = "/") => {
    if (!connection) return;
    
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/external/list/${connection.id}`,
        { params: { path } }
      );
      setFiles(response.data.items || []);
      setCurrentPath(path);
    } catch (error) {
      console.error("Failed to list files:", error);
      toast.error(error.response?.data?.detail || "Failed to load files");
    } finally {
      setIsLoading(false);
    }
  }, [connection]);

  useEffect(() => {
    if (isOpen && connection) {
      fetchFiles("/");
      setPathHistory(["/"]);
    }
  }, [isOpen, connection, fetchFiles]);

  const handleNavigate = (path) => {
    setPathHistory(prev => [...prev, path]);
    fetchFiles(path);
  };

  const handleBack = () => {
    if (pathHistory.length > 1) {
      const newHistory = [...pathHistory];
      newHistory.pop();
      const previousPath = newHistory[newHistory.length - 1];
      setPathHistory(newHistory);
      fetchFiles(previousPath);
    }
  };

  const handleFileSelect = async (item) => {
    if (item.type !== "file") return;
    
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API_URL}/external/read/${connection.id}`,
        { params: { path: item.path } }
      );
      
      onFileSelect({
        connection,
        path: item.path,
        name: item.name,
        content: response.data.content
      });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load file");
    } finally {
      setIsLoading(false);
    }
  };

  const pathParts = currentPath.split("/").filter(Boolean);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server className="w-5 h-5" />
            {connection?.name || "External Files"}
          </DialogTitle>
        </DialogHeader>

        {/* Toolbar */}
        <div className="flex items-center gap-2 border-b pb-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={handleBack}
            disabled={pathHistory.length <= 1 || isLoading}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => fetchFiles(currentPath)}
            disabled={isLoading}
          >
            <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
          </Button>
          
          {/* Breadcrumb */}
          <div className="flex items-center gap-1 flex-1 overflow-hidden">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => {
                setPathHistory(["/"]);
                fetchFiles("/");
              }}
            >
              <HardDrive className="w-4 h-4" />
            </Button>
            {pathParts.map((part, index) => (
              <div key={index} className="flex items-center gap-1">
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 truncate max-w-[100px]"
                  onClick={() => {
                    const newPath = "/" + pathParts.slice(0, index + 1).join("/");
                    const newHistory = pathHistory.slice(0, pathHistory.indexOf(newPath) + 1);
                    if (newHistory.length === 0) {
                      setPathHistory(["/", newPath]);
                    } else {
                      setPathHistory([...newHistory, newPath]);
                    }
                    fetchFiles(newPath);
                  }}
                >
                  {part}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* File List */}
        <ScrollArea className="flex-1 min-h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Folder className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground">This folder is empty</p>
            </div>
          ) : (
            <div className="space-y-1 py-2">
              {/* Sort: folders first, then files */}
              {[...files]
                .sort((a, b) => {
                  if (a.type === "folder" && b.type !== "folder") return -1;
                  if (a.type !== "folder" && b.type === "folder") return 1;
                  return a.name.localeCompare(b.name);
                })
                .map((item) => (
                  <FileItem
                    key={item.path}
                    item={item}
                    onSelect={handleFileSelect}
                    onNavigate={handleNavigate}
                  />
                ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t text-sm text-muted-foreground">
          <span>{files.length} items</span>
          <span className="uppercase text-xs">{connection?.protocol}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExternalFileBrowser;
